import { formatDateLong } from '@obras/shared';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { Card, CardHeader } from '../components/ui/Card.js';
import { ErrorState, RefreshingBar, Skeleton } from '../components/ui/Feedback.js';
import { usePainel } from '../features/painel/api.js';
import {
  ActivityList,
  AlertStrip,
  AttentionByFiscal,
  CardLink,
  KpiRow,
  MonthlyMeasured,
  NoObras,
  StatusOverview,
} from '../features/painel/secoes.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';

/** Com a forma real da tela: indicadores, faixa de atenção e os blocos de leitura. */
function PainelSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-[118px] rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-lg" />
      <Skeleton className="h-56 rounded-lg" />
      <Skeleton className="h-72 rounded-lg" />
    </div>
  );
}

/**
 * Painel do órgão.
 *
 * Cinco blocos, na ordem das perguntas de quem gere: **quanto há** (os quatro
 * indicadores), **o que pede ação hoje e de quem** (atenção, por fiscal, uma
 * linha recolhida por pessoa), **como está** (situação, visitas, vencimentos
 * e secretarias lado a lado), **como anda o dinheiro** (medições por mês) e
 * **o que aconteceu** (atividade recente). Tudo apurado no servidor, numa
 * leitura só.
 */
export function DashboardPage() {
  const { organization, may, user } = useAuth();
  const { data, isPending, isFetching, isError, error, refetch } = usePainel();

  /*
   * No painel do fiscal os números são da carteira dele; os atalhos para a
   * lista de obras levam o mesmo recorte, para o total de lá bater com o daqui.
   */
  const carteira = data?.scope === 'carteira' ? (user?.id ?? null) : null;

  const description = data
    ? data.scope === 'carteira'
      ? `Obras sob sua fiscalização em ${formatDateLong(data.today)}.`
      : `Posição do acervo de ${organization?.name ?? 'seu órgão'} em ${formatDateLong(data.today)}.`
    : 'Posição do acervo, apurada no servidor.';

  return (
    <AppShell>
      <PageHeader
        title="Painel"
        description={description}
        action={<CardLink to="/obras">Ver obras</CardLink>}
      />

      {/* Revalidação não desmonta o conteúdo: só sinaliza com a barra fina. */}
      <div className="mb-4">
        <RefreshingBar active={isFetching && !isPending} />
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <PainelSkeleton />
      ) : (
        <div className={cn('flex flex-col gap-6', isFetching && 'opacity-60')}>
          <KpiRow totals={data.totals} />

          {data.totals.obras === 0 ? (
            <Card>
              <NoObras scope={data.scope} />
            </Card>
          ) : (
            <>
              {/*
                O fiscal não é parte contratada: é o servidor designado para
                fiscalizar a execução do contrato. Por isso o agrupamento é
                "por fiscal responsável" — quem responde por cada pendência.
              */}
              <Card>
                <CardHeader
                  title="Precisa de atenção"
                  description={
                    data.scope === 'carteira'
                      ? 'Obras em curso sob sua fiscalização que pedem uma decisão hoje.'
                      : 'Obras em curso que pedem uma decisão hoje, por fiscal responsável.'
                  }
                />
                <AlertStrip alerts={data.alerts} fiscalId={carteira} />
                <AttentionByFiscal
                  attention={data.attention}
                  fiscais={data.fiscais}
                  defaultOpen={data.scope === 'carteira'}
                />
              </Card>

              <Card>
                <CardHeader
                  title="Situação"
                  description="Obras por situação, visitas, prazo de contrato e secretaria demandante."
                />
                <StatusOverview
                  totals={data.totals}
                  visits={data.visits}
                  expirations={data.expirations}
                  secretarias={data.secretarias}
                  fiscalId={carteira}
                />
              </Card>

              <Card>
                <CardHeader
                  title="Medições por mês"
                  description="Pago em cada um dos últimos doze meses, pela data do ato."
                />
                <MonthlyMeasured months={data.months} today={data.today} />
              </Card>
            </>
          )}

          <Card>
            <CardHeader
              title="Atividade recente"
              description="Últimos relatórios publicados."
              action={
                may('canAccessReports') ? (
                  <CardLink to="/relatorios">Ver relatórios</CardLink>
                ) : undefined
              }
            />
            <ActivityList rows={data.activity} />
          </Card>
        </div>
      )}
    </AppShell>
  );
}
